import {
  HeadContent,
  Outlet,
  createRootRouteWithContext,
  useRouterState,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { RouterContext } from '@/app/router/router'
import { DEFAULT_HEAD } from '@/shared/seo/seo-config'
import { useBootstrapQuery } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import { FloatingLanguageToggle } from '@/shared/i18n/components/floating-language-toggle'
import { currentOrganizationActions } from '@/features/organizations/domain/current-organization.store'
import { MainLayout } from '@/layouts/main-layout'
import { FullLayout } from '@/layouts/full-layout'
import { useAnalyticsPageView } from '@/shared/analytics/useAnalyticsPageView'
import { CookieConsentBanner } from '@/shared/analytics/consent'

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => DEFAULT_HEAD,
  component: RootComponent,
})

type LayoutKind = 'public' | 'full' | 'main'

function RootComponent() {
  useAnalyticsPageView()
  const bootstrapQuery = useBootstrapQuery()
  const selectedLayout = useRouterState({
    select: (state): LayoutKind => {
      for (let index = state.matches.length - 1; index >= 0; index -= 1) {
        const layout = (
          state.matches[index].staticData as { layout?: LayoutKind } | undefined
        )?.layout

        if (layout) {
          return layout
        }
      }

      return state.matches.length > 1 ? 'main' : 'public'
    },
  })

  useEffect(() => {
    const organizationId = bootstrapQuery.data?.organization?.id ?? null
    if (!organizationId) {
      return
    }

    currentOrganizationActions.syncWithAvailableOrganizations([organizationId])
  }, [bootstrapQuery.data?.organization?.id])

  return (
    <>
      <HeadContent />

      {selectedLayout === 'public' ? (
        <>
          <Outlet />
          <FloatingLanguageToggle />
        </>
      ) : selectedLayout === 'full' ? (
        <FullLayout>
          <Outlet />
        </FullLayout>
      ) : (
        <MainLayout>
          <Outlet />
        </MainLayout>
      )}

      <CookieConsentBanner />

      {import.meta.env.DEV && (
        <>
          <ReactQueryDevtools
            buttonPosition="bottom-right"
            initialIsOpen={false}
          />
          <TanStackRouterDevtools
            position="bottom-right"
            initialIsOpen={false}
            toggleButtonProps={{
              style: {
                right: '3.75rem',
              },
            }}
          />
        </>
      )}
    </>
  )
}
